import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Edit as EditIcon,
  Image as ImageIcon,
  Comment as CommentIcon,
  CheckBox as CheckBoxIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { calculateChecklistCompletion, updateItemCompletion, canCompleteItem } from "../utils/checklistUtils";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

/**
 * ChecklistViewer - View and interact with checklist items
 * @param {Object} checklist - Checklist object
 * @param {Function} onEdit - Edit handler
 * @param {Function} onUpdate - Update handler
 * @param {boolean} readOnly - Read-only mode
 */
const ChecklistViewer = ({ checklist, onEdit, onUpdate, readOnly = false }) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedCommentItem, setSelectedCommentItem] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);

  const completion = calculateChecklistCompletion(checklist.items);

  // Handle checkbox toggle
  const handleCheckboxToggle = (item) => {
    if (readOnly) return;
    const updatedItem = updateItemCompletion(item, !item.completed);
    const updatedItems = checklist.items.map((i) => (i.id === item.id ? updatedItem : i));
    onUpdate({ ...checklist, items: updatedItems });
  };

  // Handle image upload
  const handleImageUpload = async (item, file) => {
    if (readOnly || !file) return;

    setUploadingImage(true);
    try {
      // Create unique file path
      const timestamp = Date.now();
      const fileName = `${item.id}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `checklists/${fileName}`);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update item with image URL and path
      const updatedItem = {
        ...item,
        imageUrl: downloadURL,
        imagePath: `checklists/${fileName}`,
        completed: true, // Auto-complete when image uploaded
      };

      const updatedItems = checklist.items.map((i) => (i.id === item.id ? updatedItem : i));
      onUpdate({ ...checklist, items: updatedItems });
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle view image
  const handleViewImage = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setImageDialogOpen(true);
  };

  // Handle open comment dialog
  const handleOpenCommentDialog = (item) => {
    setSelectedCommentItem(item);
    setCommentText(item.comment || "");
    setCommentDialogOpen(true);
  };

  // Handle save comment
  const handleSaveComment = () => {
    if (readOnly || !selectedCommentItem) return;

    const updatedItem = {
      ...selectedCommentItem,
      comment: commentText.trim(),
      completed: commentText.trim() !== "",
    };

    const updatedItems = checklist.items.map((i) =>
      i.id === selectedCommentItem.id ? updatedItem : i
    );
    onUpdate({ ...checklist, items: updatedItems });
    setCommentDialogOpen(false);
  };

  // Get icon for item type
  const getItemIcon = (type) => {
    switch (type) {
      case "checkbox":
        return <CheckBoxIcon />;
      case "image_required":
        return <ImageIcon />;
      case "comment_required":
        return <CommentIcon />;
      default:
        return <CheckBoxIcon />;
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">{checklist.name}</Typography>
          {!readOnly && (
            <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
              Edit
            </Button>
          )}
        </Box>

        {/* Progress bar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {completion}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completion}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: "grey.200",
              "& .MuiLinearProgress-bar": {
                bgcolor: completion === 100 ? "success.main" : "primary.main",
              },
            }}
          />
        </Box>

        {/* Items list */}
        {checklist.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
            No items in this checklist. Click Edit to add items.
          </Typography>
        ) : (
          <List>
            {checklist.items.map((item, index) => (
              <ListItem
                key={item.id}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: item.completed ? "success.lighter" : "background.paper",
                }}
              >
                <ListItemIcon>{getItemIcon(item.type)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: item.completed ? "line-through" : "none",
                          color: item.completed ? "text.secondary" : "text.primary",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Chip
                        label={item.type.replace("_", " ")}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {item.type === "checkbox" && (
                        <Checkbox
                          checked={item.completed}
                          onChange={() => handleCheckboxToggle(item)}
                          disabled={readOnly}
                          size="small"
                        />
                      )}

                      {item.type === "image_required" && (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          {item.imageUrl ? (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ImageIcon />}
                                onClick={() => handleViewImage(item.imageUrl)}
                              >
                                View Image
                              </Button>
                              {!readOnly && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  component="label"
                                  disabled={uploadingImage}
                                >
                                  Replace Image
                                  <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) =>
                                      e.target.files[0] && handleImageUpload(item, e.target.files[0])
                                    }
                                  />
                                </Button>
                              )}
                            </>
                          ) : (
                            !readOnly && (
                              <Button
                                size="small"
                                variant="contained"
                                component="label"
                                startIcon={<ImageIcon />}
                                disabled={uploadingImage}
                              >
                                {uploadingImage ? "Uploading..." : "Upload Image"}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={(e) =>
                                    e.target.files[0] && handleImageUpload(item, e.target.files[0])
                                  }
                                />
                              </Button>
                            )
                          )}
                        </Box>
                      )}

                      {item.type === "comment_required" && (
                        <Box>
                          <Button
                            size="small"
                            variant={item.comment ? "outlined" : "contained"}
                            startIcon={<CommentIcon />}
                            onClick={() => handleOpenCommentDialog(item)}
                            disabled={readOnly}
                          >
                            {item.comment ? "View/Edit Comment" : "Add Comment"}
                          </Button>
                          {item.comment && (
                            <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: "italic" }}>
                              "{item.comment.substring(0, 50)}{item.comment.length > 50 ? "..." : ""}"
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>

      {/* Image viewer dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">Image</Typography>
            <IconButton onClick={() => setImageDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImageUrl && (
            <img
              src={selectedImageUrl}
              alt="Checklist item"
              style={{ width: "100%", height: "auto", borderRadius: 8 }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Comment dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {readOnly ? "View Comment" : "Add/Edit Comment"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Enter your comment here..."
            disabled={readOnly}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          {!readOnly && (
            <Button onClick={handleSaveComment} variant="contained" disabled={!commentText.trim()}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ChecklistViewer;
